/**
 * 견적서 미리보기 모달 — 저장 직후 또는 목록에서 행 클릭 시 표시.
 *
 * 템플릿 디자인을 HTML 표로 시뮬레이션. PDF 다운로드/영업진행 연동 버튼은
 * Phase B/C 에서 활성화 (지금은 disabled).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { QuoteDetail } from '../../lib/quote-api';
import { buildQuoteRemark, quoteExcelDownloadUrl, quotePdfDownloadUrl } from '../../lib/quote-api';
import { costSheetUrl } from '../../lib/cost-sheet-api';
import { createCostSheetFromQuote } from '../../lib/cost-sheet-from-quote';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { useAuth } from '../../lib/auth-context';
import { LinkSalesModal } from './LinkSalesModal';
import { AddQuoteToPriceListModal } from './AddQuoteToPriceListModal';
import { mailSubject } from '../../lib/mail-subject';
import { MailSendButton } from '../../components/MailSendButton';

interface Props {
  open: boolean;
  quote: QuoteDetail | null;
  onClose: () => void;
  /** 제공 시 미리보기 하단에 '✏ 수정' 버튼 표시 (작성 권한자에게만) */
  onEdit?: () => void;
  /**
   * 제공 시 '📋 이 내용으로 새 견적서' 버튼 표시 (2026-07-30, 작성 권한자에게만).
   * 원본은 건드리지 않고 같은 내용의 새 견적서 작성 폼을 연다.
   */
  onCopy?: () => void;
}

const COMPANY_INFO: Record<string, { name: string; bizNo: string; addr: string; tel: string }> = {
  SM: {
    name: '(주) 에스엠시스템즈',
    bizNo: '113-86-83368',
    addr: '서울시 금천구 가산디지털2로 135, A동 1007호 (가산어반워크 1차)',
    tel: 'Tel 070-8708-4776 FAX 070-8708-4775',
  },
  EJ: {
    name: '(주) 이진아이씨티',
    bizNo: '257-86-02520',
    addr: '서울특별시 금천구 가산디지털2로 135, 에이동 10층 1009호(가산동, 가산 어반워크 1차)',
    tel: 'Tel 070-8708-4776 FAX 070-8708-4775',
  },
};

export function QuotePreviewModal({ open, quote, onClose, onEdit, onCopy }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [linkOpen, setLinkOpen] = useState(false);
  const [addPriceOpen, setAddPriceOpen] = useState(false);
  // 🧮 이 견적서로 원가분석표 만들기 (v2.236.0) — 만드는 동안 단추 잠금
  const [makingSheet, setMakingSheet] = useState(false);
  if (!open || !quote) return null;
  const goCostSheet = (id: number): void => {
    onClose();
    navigate(costSheetUrl(id));
  };
  const makeCostSheet = async (): Promise<void> => {
    if (makingSheet) return;
    setMakingSheet(true);
    try {
      const r = await createCostSheetFromQuote(quote);
      window.alert(
        `원가분석표를 만들었습니다 — 줄 ${r.total}개 · 매입 원가를 찾아 넣은 줄 ${r.matched}개.\n` +
          (r.matched < r.total ? '나머지 줄은 원가가 0 입니다 — 표에서 [+ 매입 목록에서] 로 찾거나 직접 적으세요.\n' : '') +
          '판매 단가는 견적서 값으로 고정돼 있습니다(↺ 로 마진율 자동).',
      );
      goCostSheet(r.sheet.id);
    } catch (e) {
      window.alert(`원가분석표를 만들지 못했습니다: ${(e as Error).message}`);
    } finally {
      setMakingSheet(false);
    }
  };
  const co = COMPANY_INFO[quote.companyId] ?? COMPANY_INFO.SM!;
  // 유지보수 견적서 — 표가 「단가·월 청구금액·기간·합계 금액」 이고 총계가 3줄 (v2.104.0)
  const isMaint = quote.quoteKind === '유지보수';
  const monthsOf = (it: { months?: number }) => Math.max(1, Math.round(it.months ?? 1));
  const monthlyTotal = quote.items.reduce((a, it) => a + it.lineTotal, 0);
  // mgmt 포함 — 백엔드 POST /price-list/main/bulk 은 requireRole(['sales','admin']) 이고
  // 이 검사는 '서열 이상'이라 mgmt 도 통과한다. 가격표 화면(PriceList.tsx)의 canEdit 도 mgmt 포함.
  // 여기만 빠져 있어 경영지원 계정에서 [➕ 가격표에 추가] 가 안 보였다 (2026-07-28 정정).
  const canAddToPriceList =
    user?.role === 'admin' || user?.role === 'mgmt' || user?.role === 'sales';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4"
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-4xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              견적서 미리보기
              {isMaint && (
                <span className="ml-2 align-middle inline-block px-2 py-0.5 text-xs rounded font-medium bg-teal-100 text-teal-700">
                  유지보수
                </span>
              )}
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {quote.quoteNo} · {quote.empDisplay}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-6 bg-slate-50">
          <div className="bg-white border border-slate-300 p-6 text-xs">
            {/* 회사 정보 */}
            <div className="text-right text-xs text-slate-700 mb-3 leading-tight">
              <div className="font-bold text-sm">{co.name}</div>
              <div>사업자 등록번호 : {co.bizNo}</div>
              <div>{co.addr}</div>
              <div>{co.tel}</div>
            </div>

            <h1 className="text-center text-lg font-bold tracking-[0.5em] my-4">
              견&nbsp;&nbsp;&nbsp;&nbsp;적&nbsp;&nbsp;&nbsp;&nbsp;서
            </h1>

            {/* 헤더 표 */}
            <table className="w-full border-collapse text-xs mb-2">
              <tbody>
                <tr>
                  <Td label>수&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;신 :</Td>
                  <Td colSpan={2}>{quote.recipientCompany}</Td>
                  <Td label>견 적 일 자 :</Td>
                  <Td>{quote.quoteDate}</Td>
                </tr>
                <tr>
                  <Td label>참&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;조 :</Td>
                  <Td colSpan={2}>{quote.reference}</Td>
                  <Td label>담&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;당 :</Td>
                  <Td>{quote.empDisplay}</Td>
                </tr>
                <tr>
                  <Td label>납 품 조 건 :</Td>
                  <Td colSpan={2}>협의</Td>
                  <Td label>연  락  처 :</Td>
                  <Td>{quote.contactPhone}</Td>
                </tr>
                <tr>
                  <Td label>견 적 유 효 :</Td>
                  <Td colSpan={2}>발행 후 1주일</Td>
                  <Td label>이  메  일 :</Td>
                  <Td>{quote.contactEmail}</Td>
                </tr>
                <tr>
                  <Td label>결 제 조 건 :</Td>
                  <Td colSpan={2}>현금 결제</Td>
                  <Td label>유 지 보 수 :</Td>
                  <Td>협의</Td>
                </tr>
                <tr>
                  <Td label>프로젝트명 :</Td>
                  <Td colSpan={4}>{quote.projectName}</Td>
                </tr>
                <tr>
                  <Td label>견 적 금 액 :</Td>
                  <Td colSpan={4}>
                    <span className="font-bold text-slate-800">
                      {quote.totalWithVat.toLocaleString()}
                    </span>{' '}
                    원(VAT 포함)
                  </Td>
                </tr>
              </tbody>
            </table>

            <div className="text-right text-[11px] text-slate-500 mb-1">단위: 원</div>

            {/* 품목 표 */}
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-300 px-1 py-1 text-center">품목</th>
                  <th className="border border-slate-300 px-1 py-1 text-center">상 세 내 용</th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-12">수량</th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-20">
                    {isMaint ? '단가' : '공급단가'}
                  </th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-20">
                    {isMaint ? '월 청구금액' : '공급합계'}
                  </th>
                  {isMaint ? (
                    <>
                      <th className="border border-slate-300 px-1 py-1 text-center w-16">기간</th>
                      <th className="border border-slate-300 px-1 py-1 text-center w-20">합계 금액</th>
                    </>
                  ) : (
                    <th className="border border-slate-300 px-1 py-1 text-center w-20">비고</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {quote.items.map((it) => (
                  <tr key={it.id}>
                    <td className="border border-slate-300 px-1 py-1 align-top">{it.itemName}</td>
                    <td className="border border-slate-300 px-1 py-1 align-top whitespace-pre-wrap">
                      {it.description}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right align-top">
                      {it.qty}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right align-top">
                      {it.unitPrice.toLocaleString()}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right align-top">
                      {it.lineTotal.toLocaleString()}
                    </td>
                    {isMaint ? (
                      <>
                        <td className="border border-slate-300 px-1 py-1 text-right align-top">
                          {monthsOf(it)} 개월
                        </td>
                        <td className="border border-slate-300 px-1 py-1 text-right align-top font-medium">
                          {(it.lineTotal * monthsOf(it)).toLocaleString()}
                        </td>
                      </>
                    ) : (
                      <td className="border border-slate-300 px-1 py-1 align-top">{it.note}</td>
                    )}
                  </tr>
                ))}
                {isMaint ? (
                  <>
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="border border-slate-300 px-2 py-1 text-right font-medium">
                        월 청구금액 총계 (VAT 별도)
                      </td>
                      <td className="border border-slate-300 px-2 py-1 text-right font-bold">
                        {monthlyTotal.toLocaleString()}
                      </td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="border border-slate-300 px-2 py-1 text-right font-medium">
                        계약기간 청구금액 총계 (VAT 별도)
                      </td>
                      <td className="border border-slate-300 px-2 py-1 text-right font-bold">
                        {quote.totalSupply.toLocaleString()}
                      </td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="border border-slate-300 px-2 py-1 text-right font-medium">
                        계약기간 청구금액 총계 (VAT 포함)
                      </td>
                      <td className="border border-slate-300 px-2 py-1 text-right font-bold text-emerald-700">
                        {quote.totalWithVat.toLocaleString()}
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr className="bg-slate-50">
                      <td colSpan={4} className="border border-slate-300 px-2 py-1 text-right font-medium">
                        공급가 총계 (VAT 별도)
                      </td>
                      <td className="border border-slate-300 px-2 py-1 text-right font-bold">
                        {quote.totalSupply.toLocaleString()}
                      </td>
                      <td className="border border-slate-300"></td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td colSpan={4} className="border border-slate-300 px-2 py-1 text-right font-medium">
                        공급가 총계 (VAT 포함)
                      </td>
                      <td className="border border-slate-300 px-2 py-1 text-right font-bold text-emerald-700">
                        {quote.totalWithVat.toLocaleString()}
                      </td>
                      <td className="border border-slate-300"></td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>

            {/* Remark — 유지보수는 계약기간 줄이 맨 위에 붙는다 (실제 문서와 같게) */}
            <div className="mt-2 border border-slate-300">
              <div className="bg-slate-50 px-2 py-1 border-b border-slate-300 font-medium">
                Remark
              </div>
              <div className="px-2 py-1.5 min-h-[3rem] whitespace-pre-wrap">
                {buildQuoteRemark(quote)}
              </div>
            </div>

            <div className="mt-3 text-[11px] text-slate-600 leading-relaxed">
              <div>* 백업되지 않은 데이터 손실에 대한 책임은 지지 않습니다.</div>
              {/* 에스엠 유지보수 양식엔 소유권 문구가 없다 (엑셀 템플릿과 같게) */}
              {!(isMaint && quote.companyId !== 'EJ') && (
                <div>
                  * 제품의 소유권은 대금을 전액지급 받는 시점에 귀사에 이전되며 당사의 사전서면동의 없이
                  제품의 전부 또는 일부를 제 3자에게 양도 및 담보제공, 대여, 기타 유사한 처분을 할 수 없습니다.
                </div>
              )}
            </div>
            <div className="mt-4 text-center text-[11px] text-slate-600">
              ※ 당사는 이 견적서상의 가격 및 조건들을 수용하고 이 견적서를 귀사에 대한 공식 발주서로
              대신하고자 합니다.
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="btn btn-warning-solid"
              >
                ✏ 수정
              </button>
            )}
            {onCopy && (
              <button
                type="button"
                onClick={onCopy}
                title="이 견적서 내용을 그대로 가져와 새 견적서를 작성합니다 (원본은 그대로 남습니다)"
                className="btn btn-primary"
              >
                📋 이 내용으로 새 견적서
              </button>
            )}
            <button
              type="button"
              onClick={() => openServerPdfPreview({
                pdfUrl: quotePdfDownloadUrl(quote.id),
                filename: `견적서_${quote.quoteNo}.pdf`,
                mailDoc: {
                  docRefs: [
                    { docType: 'quote', docId: quote.id, label: `견적서 ${quote.quoteNo} (PDF)` },
                    { docType: 'quote-xlsx', docId: quote.id, label: `견적서 ${quote.quoteNo} (엑셀)` },
                  ],
                  subject: mailSubject('견적서 송부', quote.projectName || quote.quoteNo),
                },
              })}
              className="btn btn-danger-solid"
            >
              📄 PDF 미리보기
            </button>
            {/* ✉ 메일 — **여기서 바로** 보낸다 (v2.175.0 · 사용자 요청 2026-09-14:
                「견적서 미리보기 상태에서 메일보내기 버튼 만들기」).
                ⭐PDF 미리보기 창의 ✉ 는 **그대로 둔다** — 둘 다 되는 것이지 옮긴 것이 아니다.
                ⚠단추줄이 창 아래라 형식 메뉴는 **위로** 펼친다(아래로 펼치면 잘린다).
                viewer 는 MailSendButton 이 스스로 안 그린다. */}
            <MailSendButton
              compose={() => ({
                docRefs: [
                  { docType: 'quote', docId: quote.id, label: `견적서 ${quote.quoteNo} (PDF)` },
                  { docType: 'quote-xlsx', docId: quote.id, label: `견적서 ${quote.quoteNo} (엑셀)` },
                ],
                subject: mailSubject('견적서 송부', quote.projectName || quote.quoteNo),
              })}
              menuDirection="up"
              label="✉ 메일로 보내기"
              title="이 견적서를 첨부한 메일을 바로 작성합니다 (PDF·엑셀 중 고름)"
              className="px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700 text-white rounded"
            />
            {/* 유지보수 견적서는 영업진행에 연동하지 않는다 — 거래명세서·납품확인증이
                「수량 × 단가」만 읽어 계약기간 총액 대신 월 청구금액이 찍히기 때문 (v2.104.0) */}
            {!isMaint && (
              <button
                type="button"
                onClick={() => setLinkOpen(true)}
                className="btn btn-primary"
              >
                🔗 영업진행 연동
              </button>
            )}
            {canAddToPriceList && !isMaint && (
              <button
                type="button"
                onClick={() => setAddPriceOpen(true)}
                title="이 견적 항목을 주요제품 판매가(가격표)에 추가"
                className="btn btn-success-solid"
              >
                ➕ 가격표에 추가
              </button>
            )}
            {/* 🧮 원가분석표 (v2.236.0) — 이어진 표가 있으면 열고, 없으면 이 견적서로 만든다(판매 단가 고정 · 원가는 품목 마스터 이름이 같은 줄만 매입가로) */}
            {canAddToPriceList && !isMaint && (
              quote.costSheetId ? (
                <button
                  type="button"
                  onClick={() => goCostSheet(quote.costSheetId!)}
                  title="이 견적서와 이어진 원가분석표를 엽니다 (가격표 ▸ 원가분석)"
                  className="btn btn-warning-solid"
                >
                  🧮 원가분석표
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { void makeCostSheet(); }}
                  disabled={makingSheet}
                  title="이 견적서의 품목으로 원가분석표를 만듭니다 — 판매 단가는 견적서 값, 원가는 매입 내역에서 찾아 넣고 마진을 봅니다"
                  className="btn btn-warning-solid"
                >
                  {makingSheet ? '만드는 중…' : '🧮 원가 분석'}
                </button>
              )
            )}
            <a
              href={quoteExcelDownloadUrl(quote.id)}
              className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded"
            >
              📊 Excel 다운로드
            </a>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
      <LinkSalesModal open={linkOpen} quote={quote} onClose={() => setLinkOpen(false)} />
      {addPriceOpen && (
        <AddQuoteToPriceListModal
          quote={quote}
          onClose={() => setAddPriceOpen(false)}
          onDone={(count) => {
            setAddPriceOpen(false);
            window.alert(`${count}건이 주요제품 판매가(가격표)에 추가되었습니다.`);
          }}
        />
      )}
    </div>
  );
}

function Td({
  children,
  label,
  colSpan,
}: {
  children?: React.ReactNode;
  label?: boolean;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={
        'border border-slate-300 px-2 py-1 ' +
        (label ? 'bg-slate-50 font-medium text-slate-600 w-28 whitespace-nowrap' : 'text-slate-800')
      }
    >
      {children}
    </td>
  );
}
